import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import { formatDate } from '../../../../application/shared/FormatedDate';
import { ProjectChangesDateLogList } from '../../../../domain/projectChangesDateLog/ProjectChangesDateLogList';
import { ProjectChangesDateLog } from '../../../../domain/projectChangesDateLog/projectChangesDateLog';
import { ProjectChangesDateLogRepository } from '../../../../infrastructure/repositories/project-changes-date-log/projectChangesDateLogRepository';
import ModalProjectChangesSection from '../form/ModalProjectChangesSection';
import '../../styles/style.css'

interface Props {
    text: string;
    maxLength?: number;
}

interface ProjectChangesDateLogTabProps {
    projectChangesDateLogList: ProjectChangesDateLogList[] | undefined;
    projectChanges: ProjectChangesDateLog | undefined;
}

export const ProjectChangesDateLogTab = ({
    projectChanges: selectedProjectChanges,
}: ProjectChangesDateLogTabProps) => {
    const initialState = selectedProjectChanges ?? {
        id: 0,
        newDate: '',
        comments: '',
        date: '',
        previousDate: '',
        projectId: 0,
        projectStatusId: 0,
        file: null,
    };

    const [modal, setModal] = useState(false);
    const [projectChanges, setProjectChanges] =
        useState<ProjectChangesDateLog>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isIncomplete, setIsIncomplete] = useState(true);
    const [projectChangesDateLog, setProjectChangesDateLog] =
        useState<ProjectChangesDateLogList[]>();
    const [selectedFile, setSelectedFile] = useState<FormData | null>(null);
    const { id } = useParams<{ id: any }>();
    const openModal = () => setModal(!modal);

    const handleInputChange = (
        name: string,
        value: string | boolean | number[] | number | undefined
    ) => {
        setProjectChanges({ ...projectChanges, [name]: value });
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const formData = new FormData();
        formData.append('file', file || '');
        formData.append('newDate', projectChanges.newDate || '');
        formData.append('comments', projectChanges.comments);
        formData.append(
            'projectStatusId',
            projectChanges.projectStatusId.toString()
        );

        setSelectedFile(formData);
    };

    const handleError = (error: any) => {
        setErrorMessage(error.response.data);
        setIsSubmitting(false);
        window.scrollTo({ behavior: 'smooth', left: 0, top: 0 });
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        setIsSubmitting(true);
        setErrorMessage('');

        if (selectedFile) {
            ProjectChangesDateLogRepository.CreateProjectChangesDateLogFromForm(
                selectedFile,
                id
            )
                .then((response) => {
                    setModal(false);
                    setIsSubmitting(false);
                    requestDateLogs();
                    Swal.fire('Novedad creada exitosamente!', '', 'success');
                })
                .catch(handleError);
        } else {
            ProjectChangesDateLogRepository.CreateProjectChangesDateLogFromBody(
                projectChanges,
                id
            )
                .then((response) => {
                    setModal(false);
                    setIsSubmitting(false);
                    requestDateLogs();
                    Swal.fire('Novedad creada exitosamente!', '', 'success');
                })
                .catch(handleError);
        }
    };

    useEffect(() => {
        if (projectChanges.comments !== '' && projectChanges.newDate !== '') {
            setIsIncomplete(false);
            setErrorMessage('');
        } else {
            setIsIncomplete(true);
            setErrorMessage('Ingresa todos los campos correctamente');
        }
    }, [projectChanges]);

    useEffect(() => {
        if (id) {
            requestDateLogs();
        }
    }, [id]);

    const requestDateLogs = () => {
        ProjectChangesDateLogRepository.GetProjectChangesDateLogList(id).then(
            (response) => {
                setProjectChangesDateLog(response);
            }
        );
    };

    const handleDownloadClick = (url: string) => {
        window.location.href = url;
    };

    /**
     * Displays a truncated version of a long text, displaying only a specified number of 
     * characters if the text exceeds the 150 character limit. 
     * Provides a message to expand or collapse the entire content.
     */
    const ExpandableText = ({ text, maxLength = 150 }: Props) => {
        const [expanded, setExpanded] = React.useState(false);
      
        if (!text) return null;
      
        const shouldTruncate = text.length > maxLength;
        const displayText = expanded || !shouldTruncate ? text : text.slice(0, maxLength) + '...';
      
        return (
          <p className='text-muted mb-0'>
            {displayText}
            {shouldTruncate && (
              <span
                onClick={() => setExpanded(!expanded)}
                className="toggle-text-link"
              >
                {expanded ? 'Ver menos' : 'Ver más'}
              </span>
            )}
          </p>
        );
      };
      

    return (
        <React.Fragment>
            <div className='row p-3'>
                <div className='col-10 d-flex justify-content-start p-1'>
                    <h5 className='card-title flex-grow-1 mb-0 my-4 px-4 fs-18'>
                        Historial de novedades
                    </h5>
                </div>
                <div className='col-2 d-flex justify-content-end align-items-center mb-1 px-4'>
                    <button
                        type='button'
                        className='btn btn-primary'
                        onClick={openModal}
                    >
                        <i className='ri-add-box-fill me-1 align-bottom'></i> Nueva
                        Novedad
                    </button>
                </div>
            </div>

            <div className='team-list list-view-filter p-4'>
                {projectChangesDateLog?.map((projectChanges) => (
                    <Card key={projectChanges.id} className='team-box'>
                        <CardBody className='px-4'>
                            <Row className='align-items-center team-row'>
                                <div className='col team-settings'></div>
                                <Col lg={5}>
                                    <div className='team-profile-img'>
                                        <i className='ri-record-circle-fill text-primary'></i>
                                        <div className='team-content'>
                                            <Link to='#' className='d-block'>
                                                <h5 className='fs-15 mb-1'>
                                                    {
                                                        projectChanges.ProjectStatusName
                                                    }{' '}
                                                    - {projectChanges.noveltyType}
                                                </h5>
                                            </Link>
                                            <ExpandableText text={projectChanges.comments} />
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={4}>
                                    <Row className='text-muted text-center'>
                                        <Col
                                            xs={6}
                                            className='border-end border-end-dashed'
                                        >
                                            <h5 className='fs-15 mb-1'>
                                                Fecha Previa
                                            </h5>
                                            <p className='text-muted mb-0'>
                                                {formatDate(
                                                    projectChanges.previousDate
                                                )}
                                            </p>
                                        </Col>
                                        <Col xs={6}>
                                            <h5 className='fs-15 mb-1'>
                                                Nueva Fecha
                                            </h5>
                                            <p className='text-muted mb-0'>
                                                {formatDate(projectChanges.newDate)}
                                            </p>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col lg={2} className='col-0 text-center'>
                                    <div className=''>
                                        <h5 className='fs-15 mb-1'>
                                            Fecha de Creación:
                                        </h5>
                                    </div>
                                    <div className='text-muted mb-0'>
                                        {formatDate(projectChanges.date)}
                                    </div>
                                </Col>
                                {projectChanges.url !== null ? (
                                    <Col lg={1} className='col-0 text-center'>
                                        <div className=''>
                                            <button
                                                type='button'
                                                className='btn btn-light'
                                                onClick={() =>
                                                    handleDownloadClick(
                                                        projectChanges.url
                                                    )
                                                }
                                            >
                                                <i className='ri-download-2-line'></i>
                                            </button>
                                        </div>
                                    </Col>
                                ) : (
                                    <Col lg={1}></Col>
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                ))}
            </div>

            <Modal isOpen={modal} toggle={openModal} centered className='border-0'>
                <ModalHeader toggle={openModal} className='p-3 ps-4 bg-soft-warning'>
                    Nueva Novedad
                </ModalHeader>
                <ModalBody className='p-4'>
                    <ModalProjectChangesSection
                        onChange={handleInputChange}
                        projectChanges={projectChanges}
                    />
                    <div className='flex-shrink-0 mt-2 d-flex align-items-center'>
                        <h4 className='card-title mb-0'>Importar Archivo</h4>
                    </div>
                    <div className='flex-shrink-0 mt-1 mx-2'>
                        <label
                            htmlFor='fileInput'
                            className='btn btn-soft-info btn-sm mt-2'
                        >
                            <i className='ri-upload-2-fill me-1 align-bottom'></i>{' '}
                            <input
                                type='file'
                                id='fileInput'
                                onChange={handleFileChange}
                            />
                        </label>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type='button'
                        className='btn btn-light w-xs'
                        data-bs-dismiss='modal'
                        onClick={() => setModal(false)}
                    >
                        Cancelar
                    </button>
                    <button
                        type='button'
                        className='btn btn-warning w-xs'
                        onClick={handleSubmit}
                        disabled={isIncomplete || isSubmitting}
                    >
                        Agregar
                    </button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};